import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Breadcrumbs from "@/components/admin/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatApiError } from "@/lib/errors";

function RoleDialog({ open, onOpenChange, role, vocab, onSaved }) {
  const isEdit = !!role;
  const [form, setForm] = useState({
    name: "",
    display_name: "",
    permissions: {},
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (role) {
      setForm({
        name: role.name,
        display_name: role.display_name || role.name,
        permissions: { ...(role.permissions || {}) },
      });
    } else {
      setForm({
        name: "",
        display_name: "",
        permissions: vocab.keys.reduce((acc, k) => ({ ...acc, [k]: false }), {}),
      });
    }
  }, [open, role, vocab.keys]);

  const togglePerm = (k) =>
    setForm((f) => ({
      ...f,
      permissions: { ...f.permissions, [k]: !f.permissions[k] },
    }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isEdit) {
        // Locked (default) roles: only display_name is mutable. Sending the
        // permissions map would trigger a 400 from the locked-role guard.
        const body = role?.is_locked
          ? { display_name: form.display_name }
          : { display_name: form.display_name, permissions: form.permissions };
        const { data } = await api.patch(`/admin/roles/${role.name}`, body);
        toast.success("Role updated");
        onSaved(data);
      } else {
        const { data } = await api.post("/admin/roles", {
          name: form.name,
          display_name: form.display_name,
          permissions: form.permissions,
          app_access: [],
        });
        toast.success("Role created");
        onSaved(data);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err, "Save failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const locked = role?.is_locked;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="role-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit role: ${role.display_name}` : "New role"}</DialogTitle>
          <DialogDescription>
            {locked
              ? "This is a default role. Permissions are locked — duplicate it as a custom role if you need tweaks."
              : "Toggle what this role can do. App visibility is managed in the matrix."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                required
                disabled={isEdit}
                value={form.name}
                onChange={(e) =>
                  setForm({
                    ...form,
                    name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""),
                  })
                }
                placeholder="e.g. dispatch"
                data-testid="role-form-name"
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label>Display name</Label>
              <Input
                required
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                data-testid="role-form-display-name"
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label>Permissions</Label>
            <div className="space-y-3 rounded-md border border-slate-200 p-3">
              {vocab.groups.map((g) => (
                <div key={g.label}>
                  <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    {g.label}
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    {g.keys.map((k) => (
                      <label
                        key={k}
                        className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50"
                      >
                        <Checkbox
                          disabled={locked}
                          checked={Boolean(form.permissions[k])}
                          onCheckedChange={() => togglePerm(k)}
                          data-testid={`role-perm-${k}`}
                        />
                        <code className="font-mono text-xs text-slate-700">{k}</code>
                      </label>
                    ))}
                  </div>
                  {g.label === "Calendar" && (
                    <p className="mt-1.5 text-[11px] leading-snug text-slate-500">
                      <span className="font-medium text-slate-600">calendar.write</span> lets a user create entries and edit/delete <span className="italic">their own</span>. Editing or deleting <span className="italic">anyone's</span> entry also requires <span className="font-medium text-slate-600">calendar.admin</span>.
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="role-form-submit"
              className="bg-cargodash-primary-600 hover:bg-cargodash-primary-700"
            >
              {submitting ? "Saving…" : isEdit ? "Save" : "Create role"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RoleCardPerms({ permissions }) {
  // Memoised to avoid re-running the filter/slice chain on every parent render.
  const activeKeys = useMemo(
    () => Object.entries(permissions || {}).filter(([, v]) => v).map(([k]) => k),
    [permissions]
  );
  const shown = activeKeys.slice(0, 6);
  const extra = activeKeys.length - shown.length;
  return (
    <div className="mt-3 flex flex-wrap gap-1">
      {shown.map((k) => (
        <code key={k} className="rounded bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] text-slate-600">
          {k}
        </code>
      ))}
      {extra > 0 && <span className="text-[10px] text-slate-400">+{extra} more</span>}
    </div>
  );
}

export default function AdminRoles() {
  const { refresh } = useAuth();
  const [roles, setRoles] = useState([]);
  const [vocab, setVocab] = useState({ keys: [], groups: [] });
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, v] = await Promise.all([
        api.get("/admin/roles"),
        api.get("/admin/roles/permissions"),
      ]);
      setRoles(r.data);
      setVocab(v.data);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const doDelete = async () => {
    const r = confirmDelete;
    setConfirmDelete(null);
    if (!r) return;
    try {
      await api.delete(`/admin/roles/${r.name}`);
      toast.success("Role deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e) || "Failed");
    }
  };

  return (
    <div data-testid="admin-roles" className="mx-auto w-full max-w-5xl">
      <Breadcrumbs />
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900">
            Roles
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {roles.length} role{roles.length === 1 ? "" : "s"} · permissions govern hub features.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          data-testid="roles-create-btn"
          className="gap-2 bg-cargodash-primary-600 hover:bg-cargodash-primary-700"
        >
          <Plus className="h-4 w-4" />
          New role
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {loading && (
          <div className="col-span-full rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
            Loading roles…
          </div>
        )}
        {!loading &&
          roles.map((r) => {
            const activeCount = Object.values(r.permissions || {}).filter(Boolean).length;
            return (
              <div
                key={r.name}
                data-testid={`role-card-${r.name}`}
                className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-cargodash-primary-500/40"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-display text-base font-semibold text-slate-900">
                        {r.display_name || r.name}
                      </h3>
                      {r.is_locked && (
                        <Lock className="h-3.5 w-3.5 text-slate-400" />
                      )}
                    </div>
                    <code className="text-[11px] font-mono text-slate-500">{r.name}</code>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditing(r);
                        setFormOpen(true);
                      }}
                      data-testid={`role-edit-${r.name}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={r.is_locked}
                      onClick={() => setConfirmDelete(r)}
                      data-testid={`role-delete-${r.name}`}
                      className="text-cargodash-alert-600 hover:bg-cargodash-alert-600/10 hover:text-cargodash-alert-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                  <Badge variant="secondary">
                    {activeCount} / {vocab.keys.length} perms
                  </Badge>
                  <span>·</span>
                  <span>
                    {r.app_access?.includes("*")
                      ? "all apps"
                      : `${r.app_access?.length || 0} app${r.app_access?.length === 1 ? "" : "s"}`}
                  </span>
                </div>
                <RoleCardPerms permissions={r.permissions} />
              </div>
            );
          })}
      </div>

      <RoleDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        role={editing}
        vocab={vocab}
        onSaved={async () => {
          await load();
          // The save may have changed the editing admin's own role perms.
          // Refresh /auth/me so the sidebar / route guards reflect the
          // change instantly without waiting for the focus/visibility
          // revalidation tick.
          await refresh({ force: true });
        }}
      />

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent data-testid="role-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete role "{confirmDelete?.display_name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. Roles with active users cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-cargodash-alert-600 hover:bg-cargodash-alert-600/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
